import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, Stack, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconChevronLeft, IconLock } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';

import dt from '~/domains/contest/domains/manage/domains/common/testing.const';
import { Text } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';
import { Drawer } from '~/domains/design-system/components/Drawer/Drawer';

import type { StaticFieldProps } from '../../types/common';
import { getFilteredSelectOptions } from '../utils/getFilteredSelectOptions';

import type { SelectModalFieldWithCustom as SelectModalFieldWithCustomType } from './types';
import { useStyles } from './styles';
import { useCustomDeadlineValues } from './hooks/useCustomDeadlineValues';
import {
  createDayFieldOptions,
  createHourFieldOptions,
  createMockField,
} from './utils/createFieldOptions';
import { CustomDeadlineOptions } from './components/CustomDeadlineOptions';
import { MainOptions } from './components/MainOptions';
import { SelectModalTitle } from './components/SelectModalTitle';

export function SelectModalFieldWithCustom({
  field,
  value,
  errors,
  onValueChange,
  sectionKey,
  isTouched,
  allValues,
  allErrors,
}: StaticFieldProps<SelectModalFieldWithCustomType>) {
  const { t } = useTranslation('contest');
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomOptions, setShowCustomOptions] = useState(false);
  const [hover, setHover] = useState(false);

  const isError = isTouched && errors;
  const { cx, classes } = useStyles();
  const theme = useMantineTheme();

  const options = getFilteredSelectOptions({
    options: field.options,
    sectionKey,
    allValues,
    allErrors,
  });

  const selectedOption = useMemo(() => {
    if (typeof value === 'string' && value.startsWith('custom_')) {
      const [, day, time] = value.split('_');
      return t('manage.common.fields.selectModalFieldWithCustom.customLabel', { day, time });
    }

    const option = options.find((o) => o.value === value);
    if (option?.label) return option?.label;
    if (typeof value === 'string' || typeof value === 'number') {
      return value.toString();
    }
    if (field?.defaultValue) return field.defaultValue.toString();
    return t('manage.common.fields.selectModalFieldWithCustom.none');
  }, [options, value, field?.defaultValue, t]);

  const handleModalClose = useCallback(() => {
    setIsOpen(false);
    setShowCustomOptions(false);
  }, []);

  const handleModalOpen = useCallback(() => {
    setIsOpen(true);
    if (typeof value === 'string' && value.startsWith('custom_')) {
      setShowCustomOptions(true);
    }
  }, [value]);

  const { customDayValue, customHourValue, handleCustomDayChange, handleCustomHourChange } =
    useCustomDeadlineValues({
      value,
      field,
      onValueChange,
      onModalClose: handleModalClose,
    });

  const handleOnChange = useCallback(
    (optionValue: string | number | boolean | { fieldKey: string }) => {
      if (optionValue === 'custom') {
        setShowCustomOptions(true);
        return;
      }

      onValueChange(optionValue);
      setIsOpen(false);
    },
    [onValueChange]
  );

  const handleBack = useCallback(() => {
    setShowCustomOptions(false);
  }, []);

  useEffect(() => {
    if (!value) return;
    if (options.find((option) => option.value === value)) return;
    if (typeof value === 'string' && value.startsWith('custom_')) return;

    onValueChange(null);
  }, [onValueChange, options, value]);

  if (options.length === 0) {
    return null;
  }

  const dayFieldOptions = createDayFieldOptions(field.customDeadlineOptions);
  const hourFieldOptions = createHourFieldOptions(field.customDeadlineOptions);
  const dayField = createMockField(field, 'custom_day', dayFieldOptions);
  const hourField = createMockField(field, 'custom_hour', hourFieldOptions);

  return (
    <div
      className={classes.wrapper}
      onBlur={() => setHover(false)}
      onFocus={() => setHover(!hover)}
      onClick={() => setHover(!hover)}
      onKeyDown={() => setHover(!hover)}
      onMouseLeave={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
      role="button"
      tabIndex={0}
    >
      <Button
        variant="text"
        data-test-id={dt.selectOption}
        disabled={field?.disabled}
        onClick={handleModalOpen}
        className={cx(classes.card, { [classes.error]: isTouched && errors })}
      >
        <Flex align="center" justify="space-between">
          <Stack spacing={0}>
            <Text variant="body-small" c={theme.colors.gray[6]}>
              {field.label}
            </Text>
            <Text variant="title-medium" c={isError ? theme.colors.red[6] : 'inherit'}>
              {isError ? errors.join(', ') : selectedOption}
            </Text>
          </Stack>

          {field?.locked ? (
            <IconLock size={24} stroke={theme.colors.gray[4]} strokeWidth={1.5} />
          ) : (
            <IconChevronDown size={18} stroke={theme.colors.gray[4]} />
          )}
        </Flex>
        {field?.locked && hover ? (
          <div className={classes.tooltip}>
            <Text className={classes.tooltipLabel} variant="body-medium">
              {field?.locked}
            </Text>
          </div>
        ) : null}
      </Button>

      <Drawer
        open={isOpen}
        onClose={showCustomOptions ? handleBack : handleModalClose}
        onToggle={setIsOpen}
        closeIcon={
          showCustomOptions ? <IconChevronLeft size={24} stroke={theme.colors.gray[4]} /> : null
        }
        title={<SelectModalTitle label={field?.label} showCustomOptions={showCustomOptions} />}
        data-test-id={dt.drawer.container}
      >
        <Stack>
          {!showCustomOptions ? (
            <MainOptions
              options={options}
              value={value}
              sectionKey={sectionKey}
              fieldKey={field.key}
              fieldDescription={field?.description}
              classes={classes}
              onOptionChange={handleOnChange}
            />
          ) : (
            <CustomDeadlineOptions
              dayField={dayField}
              hourField={hourField}
              dayFieldOptions={dayFieldOptions}
              hourFieldOptions={hourFieldOptions}
              customDayValue={customDayValue}
              customHourValue={customHourValue}
              onDayChange={handleCustomDayChange}
              onHourChange={handleCustomHourChange}
            />
          )}
        </Stack>
      </Drawer>
    </div>
  );
}
